import React, { ErrorInfo, ReactNode } from 'react';
import { ArrowClockwise, WarningCircle } from '@phosphor-icons/react';
import BrandLogo from './BrandLogo';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('mapbl failed to render:', error, errorInfo);
  }

  private reloadApp = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="relative flex min-h-screen-safe items-center justify-center overflow-hidden bg-slate-50 px-5 text-slate-900 dark:bg-slate-950 dark:text-white">
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-cyan-400/15 to-violet-500/15 blur-3xl" />
        <section role="alert" className="relative w-full max-w-md rounded-[30px] border border-slate-200/80 bg-white/90 p-7 text-center shadow-[0_30px_90px_rgba(15,23,42,0.16)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 dark:shadow-black/30 sm:p-9">
          <div className="relative mx-auto mb-6 h-16 w-16">
            <BrandLogo iconOnly className="h-full w-full drop-shadow-[0_12px_22px_rgba(7,82,213,0.22)]" />
            <span className="absolute -right-1 -top-1 grid h-7 w-7 place-items-center rounded-full border-2 border-white bg-amber-400 text-slate-950 dark:border-slate-900">
              <WarningCircle size={17} weight="fill" />
            </span>
          </div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-300">Connection interrupted</p>
          <h1 className="text-2xl font-bold tracking-tight">The map hit a rough patch</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-600 dark:text-slate-300">
            Your account and map data are safe. Reload mapbl to reconnect and pick up where you left off.
          </p>
          <button
            type="button"
            onClick={this.reloadApp}
            className="mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
          >
            <ArrowClockwise size={19} weight="bold" />
            Reload mapbl
          </button>
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
